<template>
  <div class="conversation-header">
    <div
      class="flex justify-between items-center min-h-[4rem] border-b-1 border-solid border-grey-divider-subdued pl-[1rem] pr-[1rem]"
    >
      <button aria-label="Back" class="md-1:hidden primary-focus" @click.prevent="handleBack">
        <back-icon />
      </button>
      <a v-if="profileLink" class="primary-focus" :href="profileLink" target="_blank" @click="handleProfileClick">
        <h4 class="text-h4">{{ otherUserInformation.name }}</h4>
      </a>
      <h4 v-else class="text-h4">{{ otherUserInformation.name }}</h4>
      <div class="flex items-center">
        <div
          v-if="!otherUserInformation.bannerMessage && sitterHasVisibleProfile"
          class="px-8 py-4 rounded-md"
          :class="{ 'bg-grey-background-contrast': showTooltip }"
        >
          <a
            :href="'/interview-request/' + (otherUserInformation && otherUserInformation.correspondingUserId) + '/new'"
            @mouseover="showTooltip = true"
            @mouseleave="showTooltip = false"
          >
            <AddAvailabilityIcon
              v-if="accountType === 'parent'"
              :height="24"
              :width="22"
              :color="showTooltip ? '#99005c' : '#4B4C52'"
            />
          </a>
          <img
            v-if="showTooltip"
            src="../../../../images/icons/messaging/schedule_interview_tip.svg"
            alt="Schedule Interview"
            class="z-[2] absolute right-6"
          />
        </div>
        <kebab-menu
          v-if="!otherUserInformation.accountClosed"
          :correspondingUserAccountId="otherUserInformation && otherUserInformation.correspondingUserAccountId"
          :correspondingUserId="otherUserInformation && otherUserInformation.correspondingUserId"
          :accountType="accountType"
          :currentChannelUrl="currentChannelUrl"
        />
      </div>

      <Button
        v-if="otherUserInformation.showMessageHistory"
        id="skip-to-last-message"
        variant="text"
        class="messaging-page-nav-btn right-16"
        value="Skip to last message"
      />
    </div>

    <div v-if="otherUserInformation.showTrustSafetyBanner">
      <AlertBanner :status="3">{{ otherUserInformation.bannerMessage }}</AlertBanner>
    </div>
  </div>
</template>

<script lang="ts">
import KebabMenu from './KebabMenu.vue';
import BackIcon from '../../../../graphics/icons/BackIcon.vue';
import AddAvailabilityIcon from '../../../../graphics/icons/AddAvailabilityIcon.vue';
import Button from '../../../../components/button/Button.vue';
import AlertBanner from '../../../../components/alert_banner/AlertBanner.vue';
import { sendSegmentEvent } from '../../../../lib/segment';
import { sitterDetailsForParent } from '@/entrypoints/messages';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';

export default {
  name: 'ChannelHeader',
  components: { KebabMenu, BackIcon, Button, AlertBanner, AddAvailabilityIcon },
  props: {
    otherUserInformation: {
      type: Object,
      required: true,
    },
    accountType: {
      type: String,
      required: true,
    },
    currentChannelUrl: {
      type: String,
      required: true,
    },
    onBackClick: {
      type: Function,
      default: () => {},
    },
  },
  data(): any {
    return {
      showTooltip: false,
      sitterHasVisibleProfile: false,
      sitterProviderType: null,
    };
  },
  computed: {
    profileLink(): any {
      if (this.accountType === 'sitter' && !this.otherUserInformation.accountClosed) {
        return `/open_jobs/${this.otherUserInformation.correspondingUserAccountId}`;
      }
      if (this.accountType === 'parent' && !this.otherUserInformation.accountClosed) {
        const providerType = this.sitterProviderType || this.otherUserInformation.providerType;
        return getProviderProfileUrl(this.otherUserInformation.correspondingUserId, null, providerType);
      }
      return null;
    },
  },
  watch: {
    otherUserInformation() {
      if (this.accountType === 'sitter') return;
      this.updateSitterDetails();
    },
  },
  mounted() {
    this.updateSitterDetails();
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleTabNavigation);
  },
  methods: {
    async updateSitterDetails() {
      const details = await sitterDetailsForParent(this.otherUserInformation.correspondingUserId);

      this.sitterHasVisibleProfile = details.hasVisibleProfile;
      this.sitterProviderType = details.providerType;
    },
    handleBack() {
      this.onBackClick();
    },
    handleProfileClick() {
      if (this.accountType === 'parent') {
        sendSegmentEvent('Viewed Profile in Message', {
          viewed_profile_id: this.otherUserInformation.correspondingUserId,
        });
      }
    },
  },
};
</script>
